/* eslint-disable react-hooks/exhaustive-deps */
import React, {FC, createContext, useContext, useEffect, useLayoutEffect, useState} from 'react'
import {Helmet} from 'react-helmet-async'
export interface PageLink {
  title: string
  path: string
  isActive: boolean
  isSeparator?: boolean
}

export interface PageDataContextModel {
  pageTitle?: string
  setPageTitle: (_title: any) => void
  pageActions?: string
  setPageActions: (_action: any) => void
  pageTrailingActions?: any
  setPageTrailingActions: (_action: any) => void
  pageDescription?: string
  setPageDescription: (_description: any) => void
  pageBreadcrumbs?: Array<PageLink>
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void
}

const PageDataContext = createContext<PageDataContextModel>({
  setPageTitle: (_title: string) => {},
  setPageActions: (_action: any) => {},
  setPageTrailingActions: (_action: any) => {},
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {},
  setPageDescription: (_description: string) => {},
})

const PageDataProvider: React.FC = ({children}) => {
  const [pageTitle, setPageTitle] = useState<string>('')
  const [pageActions, setPageActions] = useState(undefined)
  const [pageTrailingActions, setPageTrailingActions] = useState(undefined)
  const [pageDescription, setPageDescription] = useState<string>('')
  const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Array<PageLink>>([])
  const value: PageDataContextModel = {
    pageTitle,
    setPageTitle,
    pageActions,
    setPageActions,
    pageTrailingActions,
    setPageTrailingActions,
    pageDescription,
    setPageDescription,
    pageBreadcrumbs,
    setPageBreadcrumbs,
  }
  return <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>
}

function usePageData() {
  return useContext(PageDataContext)
}

type Props = {
  metaDescription?: string
  breadcrumbs?: Array<PageLink>
  actions?: any
  trailingActions?: any
}

const PageTitle: FC<Props> = ({
  children,
  breadcrumbs,
  actions,
  trailingActions,
  metaDescription,
}) => {
  const {setPageTitle, setPageActions, setPageTrailingActions, setPageBreadcrumbs} = usePageData()
  useLayoutEffect(() => {
    if (!children) {
      return
    }

    setPageTitle(children)

    return () => {
      setPageTitle('')
    }
  }, [children])

  useEffect(() => {
    if (actions) {
      setPageActions(actions)
    }
    return () => {
      setPageActions(undefined)
    }
  }, [actions])

  useEffect(() => {
    if (trailingActions) {
      setPageTrailingActions(trailingActions)
    }
    return () => {
      setPageTrailingActions(undefined)
    }
  }, [trailingActions])

  useEffect(() => {
    if (breadcrumbs) {
      setPageBreadcrumbs(breadcrumbs)
    }
    return () => {
      setPageBreadcrumbs([])
    }
  }, [breadcrumbs])

  const documentTitle = children ? `${children} | ShipBae` : 'ShipBae'

  return (
    <Helmet>
      <title>{documentTitle}</title>
      {metaDescription && <meta name='description' content={metaDescription} />}
    </Helmet>
  )
}

const SettingsPageSetup = (pathname: string) => {
  const header = document.getElementById('gori_header_container')
  const settingsMenu = document.getElementById('aside-footer-settings')
  const navMenu = document.getElementById('gori_aside')
  const footer = document.querySelector('div.footer')
  const settingPath = '/settings/'
  const tablet = window.innerWidth < 1400

  if (pathname.includes(settingPath) && !tablet) {
    header?.classList.add('header-settings-container')
    footer?.classList.add('settings-padding')
    settingsMenu?.classList.add('show')
    navMenu?.classList.add('solid')
  } else if (!pathname.includes(settingPath)) {
    header?.classList.remove('header-settings-container')
    footer?.classList.remove('settings-padding')
    settingsMenu?.classList.remove('show')
    navMenu?.classList.remove('solid')
  }
}

const PageDescription: React.FC = ({children}) => {
  const {setPageDescription} = usePageData()
  useEffect(() => {
    if (children) {
      setPageDescription(children)
    }
    return () => {
      setPageDescription('')
    }
  }, [children])
  return <></>
}

export {PageDescription, PageTitle, PageDataProvider, usePageData, SettingsPageSetup}
